// Track whether we've saved at the 25-minute mark

/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  SpreadsheetComponent,
  SelectEventArgs,
  BeforeSaveEventArgs,
  SaveCompleteEventArgs
} from '@syncfusion/ej2-react-spreadsheet';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal } from '@cloudscape-design/components';
import { useNavigate, useSearchParams, useBlocker } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../../context/authContext';
import { DocumentContext } from '../../../context/documentContext';
import {
  createCitationInfo,
  getAddedRows,
  getCellValue,
  getColumnValue,
  getRangeAndCells,
  groupAdjacentCells,
  hash,
  replaceColumnInRanges,
  sanitizeAnswerText
} from './helpers';
import RFPAnswerCard from './RFPAnswerCard';
import {
  documentTableItem,
  IRFP_Answer,
  IRFP_Manifest,
  IRFP_Questions,
  ISelectedCell
} from '../../../lib/models/interfaces';
import { UseUnloadDocumentWithContextAuth } from '../../../hooks/removeDocumentLock';
import { DocumentFilter } from '../Chat/documentFilter';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import { SquareX } from 'lucide-react';
import {
  getAvailableCoins,
  holdCoinsAmountRFP,
  placeHoldOnCoins
} from '../../../helpers/calculateHoldCoins';
import { useKeyPressed } from '../../../hooks/useKeyPressed';
import { useUnsavedChangesWarning } from './useUnsavedChangesWarning';
import { createLogger } from '../../../helpers/Logger';
import { useAlert } from '../../../context/alertContext';

interface ValidationObject {
  value1?: any;
  value2?: any;
  type?: any;
  validationList?: any[];
}

const logger = createLogger({ prefix: '[RFP Viewer]' });

const missingAnswerString =
  'We generated an answer, but it didn’t load properly.This usually means the response format wasn’t recognized, not that your request failed.Please try again, or contact support if this keeps happening.';
const citationRegex = /<<cid:([A-Fa-f0-9]{24})>>/g;

const SpreadsheetUploader = () => {
  // LockCountdownTimer: displays time remaining until lock expiration
  const hasSavedAt25 = useRef(false);
  const { token, auth, user } = React.useContext(UserContext);
  const { setSubsAlert } = useAlert();
  const {
    getDocument,
    getAllDocs,
    updateDocument,
    allDocuments,
    setAllDocuments,
    setMainAlert
  } = React.useContext(DocumentContext);
  const navigate = useNavigate();
  const spreadsheetRef = React.useRef<SpreadsheetComponent | null>(null);
  const virtualizationCheckId = useRef<number | null>(null);
  const isAltPressed = useKeyPressed('alt');
  const [showSheet, setShowSheet] = useState<boolean>(false);
  const [selectInputRows, setSelectedInputRows] = useState<
    {
      sheet: string;
      cells: string;
    }[]
  >([]);
  const [selectOutputRows, setSelectedOutputRows] = useState<{
    sheet: string;
    column: string;
  }>();
  const [selectingQuestions, setSelectingQuestions] = useState<boolean>(false);
  const [selectingAnswers, setSelectingAnswers] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [currentDoc, setCurrentDoc] = useState<documentTableItem | null>(null);
  const [alertA, setAlert] = useState<{
    showAlert: boolean;
    message: string;
    type: 'error' | 'success';
  }>({
    showAlert: false,
    message: '',
    type: 'success'
  });
  const [manifestValue, setManifestValue] = useState<IRFP_Manifest | null>(
    null
  );
  const [spreadsheetReady, setSpreadsheetReady] = useState(false);
  const [fileValue, setFileValue] = useState<File | null>(null);
  const [selectedCell, setSelectedCell] = useState<ISelectedCell>(
    {} as ISelectedCell
  );
  const [selectedCellsForAnswers, setSelectedCellsForAnswers] =
    useState<ISelectedCell>();
  const [currentSelectedCellAnswer, setCurrentSelectedCellAnswer] =
    useState<IRFP_Questions | null>(null);
  const [answerObject, setAnswerObject] = useState<{
    [key: string]: IRFP_Questions;
  } | null>(null);
  const [loadingDocs, setLoadingDocs] = useState<boolean>(true);
  const [docLockedByOther, setDocLockedByOther] = useState<boolean>(false);
  const [showDocLockModal, setShowDocLockModal] = useState<boolean>(false);
  const [currentLock, setCurrentLock] = useState<any>();
  const [remainingTime, setRemainingTime] = useState<{
    minutes: number;
    seconds: number;
  }>();
  const [showTimeWarning, setTimeWarning] = useState<boolean>(false);
  const [warningColor, setWarningColor] = useState<string>('yellow');

  // loading state
  const [canUpload, setCanUpload] = useState<boolean>(false);
  const [uploadProcessing, setUploadProcessing] = useState<boolean>(false);

  // state for document selection
  const [KBChecked, setKBChecked] = useState<boolean>(false);
  const [kbFilters, setKBFilters] = useState<OptionDefinition[]>([]);
  const [prevChecked, setPrevChecked] = useState<boolean>(false);
  const [selectedDocuments, setSelectedDocuments] = useState<
    OptionDefinition[]
  >([]);
  const [finalizeLoading, setFinalizeLoading] = useState<boolean>(false);
  const [draftSaveLoading, setDraftSaveLoading] = useState<boolean>(false);
  const [hasOpenError, setHasOpenError] = useState<{
    showError: boolean;
    message: string;
  }>({ showError: false, message: '' });
  const [similarityThreshold, setSimilarThreshold] = useState<number>(0.8);
  const [coinsModal, setCoinsModal] = useState<boolean>(false);
  const [coinsRequired, setCoinsRequired] = useState<number>(0);
  const [availableCoins, setAvailableCoins] = useState<number>(0);
  const [InputRowsSet, setInputRowsSet] = useState<Set<string>>(new Set());
  const [inputSelected, setInputSelected] = useState<boolean>(false);
  const [outputSelected, setOutputSelected] = useState<boolean>(false);
  const [answerColumn, setAnswerColumn] = useState<string>('');
  const [originalJson, setOriginalJson] = useState<any | null>(null);
  const [userChangesNotSaved, setUserChangesNotSaved] =
    useState<boolean>(false);
  const [allAnswersValidated, setAllAnswersValidated] =
    useState<boolean>(false);
  const [highLight, setHighLight] = useState<boolean>(false);
  const [showCoinsModal, setShowCoinsModal] = useState<{
    visible: boolean;
    type: string;
  }>({ visible: false, type: '' });

  const columnLetterToIndex = (column: string): number =>
    column
      .toUpperCase()
      .split('')
      .reduce((acc, char) => acc * 26 + (char.charCodeAt(0) - 64), 0) - 1;

  UseUnloadDocumentWithContextAuth(currentDoc?._id!);

  const getHashValues = async () => {
    logger.info('oHashing');
    const oHash = await hash(originalJson);
    const newHash = await hash(manifestValue);
    logger.info('oHashO', oHash);
    logger.info('oHash New', newHash);
    if (oHash !== newHash) {
      logger.info('setting changes');
      setUserChangesNotSaved(true);
    }
  };

  useEffect(() => {
    if (!manifestValue || !originalJson) return;
    getHashValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestValue, originalJson]);

  useUnsavedChangesWarning(userChangesNotSaved);

  if (hasOpenError.showError) {
    throw new Error(`${hasOpenError.message}`);
  }
  const handleOpenComplete = (e: any) => {
    setShowSheet(true);
    setSpreadsheetReady(false);
    setUserChangesNotSaved(false);
  };

  // Wait until Syncfusion's virtual scroll helpers exist before enabling downstream updates.
  useEffect(() => {
    if (!showSheet || spreadsheetReady) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const checkVirtualDomReady = () => {
      const instance = spreadsheetRef.current;
      if (!instance) {
        virtualizationCheckId.current =
          window.requestAnimationFrame(checkVirtualDomReady);
        return;
      }

      const { allowScrolling } = instance as { allowScrolling?: boolean };
      const mainContent = instance.getMainContent?.();
      const scrollElement = instance.getScrollElement?.();
      const virtualTrack = mainContent?.querySelector('.e-virtualtrack');
      const scrollReady =
        !allowScrolling ||
        (!!scrollElement && !!scrollElement.firstElementChild);

      if (virtualTrack && scrollReady) {
        virtualizationCheckId.current = null;
        setSpreadsheetReady(true);
        return;
      }

      virtualizationCheckId.current =
        window.requestAnimationFrame(checkVirtualDomReady);
    };

    checkVirtualDomReady();

    return () => {
      if (virtualizationCheckId.current !== null) {
        window.cancelAnimationFrame(virtualizationCheckId.current);
        virtualizationCheckId.current = null;
      }
    };
  }, [showSheet, spreadsheetReady]);

  const onActionComplete = React.useCallback((args: any) => {
    // requestType values for changes (not exhaustive, but covers common edits)
    const mutating = new Set([
      'cellSave',
      'format',
      'numberFormat',
      'wrap',
      'border',
      'clear',
      'paste',
      'cut',
      'clipboard',
      'insert',
      'delete',
      'merge',
      'unmerge',
      'sort',
      'filter',
      'hyperlink',
      'image',
      'chart',
      'conditionalFormat',
      'dataValidation',
      'renameSheet',
      'removeSheet',
      'updateCell',
      'updateRange',
      'resize',
      'freeze',
      'undo',
      'redo'
    ]);
    if (mutating.has(args?.requestType)) setUserChangesNotSaved(true);
  }, []);

  const addRows = (newValue: string) => {
    const [start, end] = newValue.split(':').map((s) => s.trim());
    if (!start || !end) return [];
    const newResult = getAddedRows(start, end);
    setInputRowsSet((prevSet) => new Set([...prevSet, ...newResult]));
  };
  const replaceInputSet = (rawRange: string) => {
    const [start, end] = rawRange.split(':').map((s) => s.trim());
    logger.info(`In replace input set, ${start}  ;;; ${end}`);
    if (!start || !end) return [];
    const result = getAddedRows(start, end);
    logger.info('In replace set: Result:', result);
    logger.info('new set new', new Set(result));
    setInputRowsSet(new Set(result));
  };

  const openFile = async (fileValue: File) => {
    try {
      const testResult = await axios.get(
        'https://spreadsheet.korucompliance.com/api/test'
      );
      if (testResult.status === 200) {
        const result = await spreadsheetRef.current?.open({ file: fileValue });
      }
    } catch (e) {
      logger.error('err in  open', e);
      setHasOpenError({
        showError: true,
        message:
          'There was an error opening this file. If the problem persists please contact support'
      });
    }
  };
  useEffect(() => {
    if (fileValue && spreadsheetRef) {
      // spreadsheetRef.current?.open({ file: fileValue });
      try {
        openFile(fileValue);
      } catch (e) {
        throw Error('Caught in useEffect');
      }
    }
  }, [fileValue, spreadsheetRef]);

  useEffect(() => {
    const inputCells = selectInputRows[0]?.cells;
    const outputCells = selectOutputRows?.column;
    if (!inputCells || !outputCells) return;

    const inputRange = getRangeAndCells(inputCells);
    const outputRange = getRangeAndCells(outputCells);

    if (
      outputRange?.startRow !== inputRange?.startRow ||
      outputRange?.endRow !== inputRange?.endRow
    ) {
      setAlert({
        showAlert: true,
        message: 'The Cell ranges do not match',
        type: 'error'
      });
    }
    if (
      alertA.message === 'The Cell ranges do not match' &&
      outputRange?.startRow === inputRange?.startRow &&
      outputRange?.endRow === inputRange?.endRow
    ) {
      setAlert({
        showAlert: false,
        message: '',
        type: 'success'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectInputRows, selectOutputRows]);

  const handleCellSelect = async (args: SelectEventArgs) => {
    const spreadsheet = spreadsheetRef.current;
    const activeSheet = spreadsheet?.getActiveSheet();
    const sheetName = activeSheet?.name;
    logger.info('Cell Select args::', args);
    logger.info('is altpressed', isAltPressed);
    if (!inputSelected) {
      if (isAltPressed) {
        addRows(args.range);
      } else {
        replaceInputSet(args.range);
      }
    }
    // set output column is the input is already set.
    if (!currentDoc?.processed && inputSelected && !outputSelected) {
      const start = args.range.split(':')[0];
      const startCol = start.match(/^[A-Za-z]+/)![0];
      // setOutputRowSet(new Set(startCol));
      // setSelectedCellsForAnswers({ sheet: sheetName!, cell: args.range! });
      setAnswerColumn(startCol);
    }

    if (args.name === 'select') {
      const range = args.range.split(':');

      setSelectedCell({
        sheet: sheetName!,
        cell: `${range[0]}`
      });
    }
  };

  const handleBeforeSave = async (args: BeforeSaveEventArgs) => {
    args.needBlobData = true; // To trigger the saveComplete event.
    args.isFullPost = false; // Get the spreadsheet data as blob data in the saveComplete event.
  };

  const saveExcelFile = async (blob: Blob) => {
    try {
      const currentSaveDocumentName = `${currentDoc?._id}.${currentDoc?.fileExt}`;
      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=uploadRFP`,
        {
          documentId: currentSaveDocumentName
        },
        auth
      );
      const putResult = await axios.put(presignedUrl.data.url, blob, {
        headers: {
          'Content-Type':
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
    } catch (e) {
      logger.error('error from save excel file', e);
    }
  };

  const saveComplete = async (args: SaveCompleteEventArgs) => {
    // Convert blob data to base64 string.
    logger.info('running save complete');
    const blob = args.blobData;
    setDraftSaveLoading(true);
    // uplaod to s3
    await saveExcelFile(blob);
    await saveManifestFile('uploadRFPManifestResave');
    setOriginalJson(deepClone(manifestValue));
    setUserChangesNotSaved(false);
    setDraftSaveLoading(false);
  };
  const handleSaveDocument = async () => {
    const currentSaveDocumentName = `${currentDoc?._id}.${currentDoc?.documentFileName.split('.')[1]}`;
    try {
      spreadsheetRef.current?.save({
        saveType: 'Xlsx',
        fileName: currentSaveDocumentName
      });
    } catch (e) {
      setAlert({
        showAlert: true,
        message: 'Document Save Error',
        type: 'error'
      });
    }
  };

  useEffect(() => {
    const someDocsSelected =
      KBChecked ||
      prevChecked ||
      selectedDocuments.length > 0 ||
      currentDoc?.documentType === 'prev-rfp';
    // check to see if the answers column and the questions column are different
    if (inputSelected && outputSelected && someDocsSelected) {
      setCanUpload(true);
    } else {
      setCanUpload(false);
    }
  }, [
    inputSelected,
    outputSelected,
    KBChecked,
    prevChecked,
    selectedDocuments,
    currentDoc
  ]);

  const testAnswerValidation = async () => {
    if (!spreadsheetRef?.current) {
      return;
    }
    const questionsArray = [];
    for (const cell of InputRowsSet) {
      const rowNumber = parseInt(cell.match(/\d+$/)![0], 10);
      const sheet = spreadsheetRef.current?.getActiveSheet();
      const sheetName = sheet.name;
      const range = `${cell}:${cell}`;
      const answerRange = `${answerColumn}${rowNumber}:${answerColumn}${rowNumber}`;
      const dataMapQ = await spreadsheetRef?.current?.getData(
        `${sheetName}!${range}`
      );
      const tempData = dataMapQ?.get(`${cell}`);
      if (!tempData?.value) {
        continue;
      }
      const dataMapA = await spreadsheetRef?.current?.getData(
        `${sheetName}!${answerRange}`
      );
      logger.info('dataMapA', dataMapA);
      const tempDataAnswerCell = dataMapA?.get(`${answerColumn}${rowNumber}`);
      logger.info('tempDataAnswerCell', tempDataAnswerCell);
      const temp = {
        questionText: tempData?.value ?? '',
        questionCell: cell,
        questionSheet: sheetName,
        answerCell: `${answerColumn}${rowNumber}`,
        answer: [],
        answerSheet: sheetName,
        answerText: tempDataAnswerCell?.value,
        answerValidation: tempDataAnswerCell?.validation,
        answerValidationList: [] as any[],
        userValidated: false,
        validatedBy: []
      };
      if (temp?.answerValidation?.type === 'List') {
        const listValues: any[] = [];
        const validate = temp?.answerValidation;
        if (validate) {
          if (validate.value1) {
            const match = validate.value1.match(/^=([^!]+)!(.+)$/);
            if (match) {
              const sheet = match[1]; // "Sheet2"
              const range = match[2]; // "$A$1:$A$2"
              const listValue = await spreadsheetRef?.current?.getData(
                `${sheet}!${range}`
              );
              listValue?.forEach((listCell) => {
                listValues.push(listCell.value);
              });
            }
          }
        }
        temp.answerValidationList = listValues;
      }
      questionsArray.push(temp);
    }
    logger.info('Final Questions Array', questionsArray);
  };
  const createDataForUpload = async () => {
    logger.info(spreadsheetRef?.current?.getActiveSheet());
    if (!spreadsheetRef?.current) {
      return;
    }
    setUploadProcessing(true);
    const questionsArray = [];
    for (const cell of InputRowsSet) {
      const rowNumber = parseInt(cell.match(/\d+$/)![0], 10);
      const sheet = spreadsheetRef.current?.getActiveSheet();
      const sheetName = sheet.name;
      const range = `${cell}:${cell}`;
      const answerRange = `${answerColumn}${rowNumber}:${answerColumn}${rowNumber}`;
      const dataMapQ = await spreadsheetRef?.current?.getData(
        `${sheetName}!${range}`
      );
      const tempData = dataMapQ?.get(`${cell}`);
      if (!tempData?.value) {
        continue;
      }
      const dataMapA = await spreadsheetRef?.current?.getData(
        `${sheetName}!${answerRange}`
      );
      const tempDataAnswerCell = dataMapA?.get(`${answerColumn}${rowNumber}`);
      logger.info();
      const temp = {
        questionText: tempData?.value ?? '',
        questionCell: cell,
        questionSheet: sheetName,
        answerCell: `${answerColumn}${rowNumber}`,
        answer: [],
        answerSheet: sheetName,
        answerText: tempDataAnswerCell?.value,
        answerValidation: tempDataAnswerCell?.validation,
        answerValidationList: [] as any[],
        userValidated: false,
        validatedBy: []
      };
      if (temp?.answerValidation?.type === 'List') {
        const listValues: any[] = [];
        const validate = temp?.answerValidation;
        if (validate) {
          if (validate.value1) {
            const match = validate.value1.match(/^=([^!]+)!(.+)$/);
            if (match) {
              const sheet = match[1]; // "Sheet2"
              const range = match[2]; // "$A$1:$A$2"
              const listValue = await spreadsheetRef?.current?.getData(
                `${sheet}!${range}`
              );
              listValue?.forEach((listCell) => {
                listValues.push(listCell.value);
              });
            }
          }
        }
        temp.answerValidationList = listValues;
      }
      questionsArray.push(temp);
    }
    logger.info('Final Questions Array', questionsArray);

    try {
      const searchDocs = {
        KBChecked,
        kbFilters: kbFilters.map((ele) => ele.value),
        prevChecked,
        selectedDocuments: selectedDocuments.map((ele) => ele.value),
        thresholdScore: similarityThreshold
      };
      // get and place hold on credits for the rfp.
      let enoughCredit = false;
      const creditHold = await axios.post(
        `${process.env.REACT_APP_KORU_API}/credit?type=createCreditHold`,
        {
          type: 'rfp',
          documentId: currentDoc?._id,
          kbChecked: searchDocs.KBChecked,
          numOfQuestions: questionsArray.length,
          documentIds: searchDocs.selectedDocuments
        },
        auth
      );
      console.log('credit hold === ', creditHold);
      if (creditHold?.data?.result?._id) {
        enoughCredit = true;
      }
      if (enoughCredit) {
        const presignedUrl = await axios.post(
          `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=uploadRFPManifest`,
          {
            documentId: currentDoc?._id
          },
          auth
        );
        // upload file
        if (presignedUrl.data.url === 'locked') {
          setCurrentLock(presignedUrl.data.lock[0]);
          throw Error('File Locked by other user');
        }

        const jsonPayload = JSON.stringify({
          questions: questionsArray,
          searchDocs
        });
        const blob = new Blob([jsonPayload], { type: 'application/json' });
        const putResult = await axios.put(presignedUrl.data.url, blob, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const allDocsTemp = allDocuments;
        const updatedDocs = allDocsTemp.reduce((acc, curr) => {
          if (curr._id === currentDoc?._id) {
            curr.status = 'building answers';
          }
          acc.push(curr);
          return acc;
        }, [] as documentTableItem[]);
        setAllDocuments(updatedDocs);
        setUploadProcessing(false);
        navigate(
          `/app/projects/${currentDoc?.project}/folder/${currentDoc?.folder}`
        );
      } else {
        setUploadProcessing(false);
        setShowCoinsModal({ visible: true, type: 'BuyMore' });
      }
    } catch (e) {
      setUploadProcessing(false);
      const error = e as Error;
      setMainAlert({
        message: `${error.message}`,
        showAlert: true,
        alertType: 'error'
      });
      throw Error('Upload failed');
    }
  };

  const cellSave = (args: any) => {
    if (!manifestValue) return;
    logger.info('cell save args', args);

    if (args.oldValue !== args.value) {
      // check if it is an answer cell.
      // if(args.address)
      // update the manifest file with the altered answer`
      if (answerObject?.hasOwnProperty(args.address)) {
        const add = args.address.split('!');
        const temp = manifestValue;
        const idx = temp.questions.findIndex(
          (ele: { answerSheet: string; answerCell: string }) =>
            ele.answerSheet === add[0] && ele.answerCell === add[1]
        );
        logger.info('index of anser', idx);
        if (idx) {
          temp.questions[idx].answer.push({
            text: args.value,
            user: user.name
          });
          temp.questions[idx].userValidated = true;
          temp.questions[idx].validatedBy.push(user.name);
          logger.info('temp at index', temp.questions[idx]);
          setManifestValue({ ...temp });
        }
      }
      setUserChangesNotSaved(true);
    }
  };

  useEffect(() => {
    if (!manifestValue) return;
    createAnswerObject();
    if (showSheet) {
      highLightNonValidatedCells();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestValue]);
  useEffect(() => {
    const spreadsheet = spreadsheetRef.current;
    if (!manifestValue || !spreadsheetReady || !spreadsheet) return;
    highLightNonValidatedCells();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highLight]);
  /**
   * This gets and snd downloads the spreadsheet file.
   *
   * @param {string} docId – the _id o fthe document record.
  
   * @returns {void} – returns nothing, but sets the file on state.
   *
   * @throws {ErrorType} – could throw an error if the file is not found..
   */
  const getLink = async (docId: string) => {
    if (!user) return;
    try {
      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getDocumentRFP`,
        {
          documentId: `${docId}`
        },
        auth
      );
      logger.info('presigned url', presignedUrl);
      if (presignedUrl?.data?.lock) {
        setCurrentLock(presignedUrl.data.lock[0]);
        logger.info('lock info', presignedUrl.data.lock);
        const userISLockowner = presignedUrl.data.lock[0]?.sub === user.sub;
        logger.info('is lock owner', userISLockowner);
        logger.info('user', user);

        if (!userISLockowner) {
          logger.info('setting show modal');
          setDocLockedByOther(true);
          setShowDocLockModal(true);
        } else {
          setDocLockedByOther(false);
          setShowDocLockModal(false);
        }
      }

      const response = await axios.get(presignedUrl.data.url, {
        responseType: 'blob'
      });
      const file = new File([response.data], `s3-file.xlsx`, {
        type: response.data.type
      });
      setFileValue(file);
    } catch (e) {
      setMainAlert({
        alertType: 'error',
        message: 'Error getting file.',
        showAlert: true
      });
    }
  };

  const createAnswerObject = () => {
    const tempObject: { [key: string]: IRFP_Questions } = {};
    const jsonFile = manifestValue;
    if (!jsonFile) return;
    for (let i = 0; i < jsonFile?.questions.length; i++) {
      const keyValue = `${jsonFile?.questions[i]?.answerSheet}!${jsonFile?.questions[i]?.answerCell}`;
      tempObject[keyValue] = jsonFile.questions[i];
    }

    setAnswerObject(tempObject);
    const userValidatedArray: boolean[] = [];
    Object.keys(tempObject).forEach((ele) => {
      userValidatedArray.push(tempObject[ele].userValidated);
    });

    const answersValidated = userValidatedArray.every((ele: boolean) => ele);
    if (answersValidated) {
      setAllAnswersValidated(true);
    }
  };

  const deepClone = <T,>(x: T): T => structuredClone(x);
  const getManifestFile = async (docId: string) => {
    if (!docId) return;
    logger.info('getting manifest file');
    try {
      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getDocumentRFPManifest`,
        {
          documentId: `${docId}`
        },
        auth
      );

      const response = await axios.get(presignedUrl.data.url);
      // update the answer cell with the generated answers.
      const jsonFile = response.data;
      if (jsonFile) {
        //! this is the original to check for changes
        setOriginalJson(deepClone(jsonFile));
        // we have a valid jsonFile set on state.
        setManifestValue(jsonFile);
        // set the answers on the object in state

        setLoadingDocs(false);
      } else {
        setLoadingDocs(false);
      }
    } catch (e) {
      setMainAlert({
        alertType: 'error',
        message: 'Error getting manifest file.',
        showAlert: true
      });
      setLoadingDocs(false);
    }
  };
  useEffect(() => {
    if (!token) return;
    const docId = searchParams.get('documentId');
    if (docId) {
      setup(docId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // this will perform the following:
  // 1. Get the document from Mongo
  // 2. get the xlsx document form s3
  // check if document processed, is true get the manifest file to load.
  const setup = async (docId: string) => {
    try {
      // return the document in question.
      const document = await getDocument(docId);
      console.log('document or error', document);
      if (axios.isAxiosError(document)) {
        setHasOpenError({
          showError: true,
          message: 'unauthorizedForDocument'
        });
      }
      if (document) {
        setCurrentDoc(document);
        // we have a valid document record now get the rest.
        logger.info('document', document);
        getLink(document._id);
        if (
          document.processed ||
          document.documentType === 'prev-rfp' ||
          document.documentType === 'GapAnalysis' ||
          document.documentType === 'rfp'
        ) {
          await getManifestFile(document._id);
        } else {
          setLoadingDocs(false);
        }
      }
    } catch (e) {
      throw Error(
        'There was a issue loading this page. Please refresh your browser and try again. If this issue persists please contact'
      );
    }
  };
  useEffect(() => {
    if (!token) return;
    const docId = searchParams.get('documentId');
    logger.info('document id on line 851', docId);
    if (docId) {
      setup(docId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const setAnswerValues = async () => {
    logger.info('Setting answers');
    const spreadsheet = spreadsheetRef?.current;

    if (
      !spreadsheet ||
      !spreadsheet.sheets ||
      !spreadsheet.sheets.length ||
      answerObject === null
    ) {
      logger.warn(
        'Spreadsheet not fully loaded yet. Skipping setAnswerValues.'
      );
      return;
    }
    logger.info('ready to set answer', answerObject);

    const answerKeys = Object.keys(answerObject);
    for (const answer of answerKeys) {
      const dataMap = await spreadsheetRef?.current?.getData(`${answer}`);
      if (!dataMap) {
        continue;
      }
      const cellValue = getCellValue(dataMap);

      const validation = answerObject[answer]?.validation as any;
      const userValidated = answerObject[answer]?.userValidated;
      logger.info('validation', validation);
      if (
        (!Array.isArray(validation) && validation?.text === 'Pass') ||
        userValidated ||
        currentDoc?.documentType === 'GapAnalysis'
      ) {
        const answerText = (answerObject[answer].answer.at(
          -1
        ) as IRFP_Answer) ?? {
          text: 'nothing to see'
        };
        logger.info('answerText', answerText);
        // if there is a null value for answerText.text replace with missAnswerString;
        // also get the textInternal if not null and remove the citations. Populate the call with that value.
        let textToUse = answerText.text ?? missingAnswerString;
        if (answerText?.text === null && answerText?.textInternal) {
          textToUse = answerText.textInternal.replace(citationRegex, '');
        }
        const sanitizedText = sanitizeAnswerText(textToUse);
        logger.info('sanitized text', sanitizedText);
        spreadsheet.updateCell(
          {
            value: sanitizedText
          },
          answer
        );

        const [sheetNameFromSplit, cellAddressFromSplit] = answer.split('!');
        const cellAddress = cellAddressFromSplit ?? sheetNameFromSplit;
        const sheetName =
          cellAddressFromSplit !== undefined ? sheetNameFromSplit : undefined;
        const columnLetters = cellAddress?.replace(/[0-9]/g, '') ?? '';
        const columnIndex = columnLetters
          ? columnLetterToIndex(columnLetters)
          : -1;
        if (columnIndex > -1) {
          const sheetIndex = spreadsheet.sheets?.findIndex(
            (sheet) => sheet.name === sheetName
          );
          const words = sanitizedText.trim().split(/\s+/).filter(Boolean);
          const wordsPerLine = 10;
          const snippet =
            words.length >= wordsPerLine
              ? words.slice(0, wordsPerLine).join(' ')
              : words.join(' ');
          const targetCharLength = snippet.length || wordsPerLine * 5;
          const approxWidth = Math.max(160, targetCharLength * 7);
          spreadsheet.setColWidth(
            approxWidth,
            columnIndex,
            sheetIndex !== undefined && sheetIndex > -1 ? sheetIndex : undefined
          );
        }
        spreadsheet.wrap(answer, true);
        spreadsheet.cellFormat({ verticalAlign: 'top' }, answer);

        const rowMatch = cellAddress?.match(/\d+/);
        if (rowMatch) {
          const rowNumber = parseInt(rowMatch[0], 10);
          const rowRange =
            sheetName !== undefined
              ? `${sheetName}!${rowNumber}:${rowNumber}`
              : `${rowNumber}:${rowNumber}`;
          spreadsheet.autoFit(rowRange);
        }
      } else {
        logger.info('this seems to be an issue');
        spreadsheet.cellFormat({ border: '2px solid orange' }, answer);
      }
    }
  };
  useEffect(() => {
    const readyToSetAnswers =
      spreadsheetReady &&
      manifestValue?.questions &&
      manifestValue?.questions?.length > 0 &&
      spreadsheetRef.current &&
      answerObject !== null;

    if (readyToSetAnswers) {
      logger.info(
        'We are ready to set answers ----------->',
        readyToSetAnswers
      );
      setAnswerValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadsheetReady, manifestValue, spreadsheetRef.current, answerObject]);

  const acceptAnswer = (prevAnswerIndex?: number) => {
    const spreadsheet = spreadsheetRef.current;
    const cellAddress = `${selectedCell.sheet}!${selectedCell.cell}`;
    if (!spreadsheet || !cellAddress || !manifestValue) return;

    //sanitize the text for placing in the cell.
    let answer;
    if (!prevAnswerIndex) {
      answer = currentSelectedCellAnswer?.answer.at(-1);
    } else {
      answer = {
        text: currentSelectedCellAnswer?.previousAnswer[prevAnswerIndex].answer,
        user: user.name
      };
    }
    logger.info('accept answer - answer', answer);
    const sanitizedText = sanitizeAnswerText(answer?.text!);

    spreadsheet.updateCell(
      {
        value: sanitizedText
      },
      cellAddress
    );
    spreadsheet.cellFormat({ border: 'None' }, cellAddress);

    const temp = manifestValue;
    const record = temp.questions.findIndex(
      (ele: { answerSheet: string; answerCell: string }) =>
        ele.answerSheet === selectedCell.sheet &&
        ele.answerCell === selectedCell.cell
    );
    if (record !== -1) {
      temp.questions[record].answer.push({
        text: sanitizedText,
        user: user.name
      });
      temp.questions[record].userValidated = true;
      temp.questions[record].validatedBy.push(user.name);

      setManifestValue({ ...temp });
    }
  };

  const validateAnswer = () => {
    const spreadsheet = spreadsheetRef.current;
    const cellAddress = `${selectedCell.sheet}!${selectedCell.cell}`;
    if (!spreadsheet || !cellAddress || !manifestValue) return;
    const temp = manifestValue;
    const record = temp.questions.findIndex(
      (ele: { answerSheet: string; answerCell: string }) =>
        ele.answerSheet === selectedCell.sheet &&
        ele.answerCell === selectedCell.cell
    );
    if (record !== -1) {
      temp.questions[record].userValidated = true;
      temp.questions[record].validatedBy.push(user.name);

      setManifestValue({ ...temp });
    }
  };

  useEffect(() => {
    if (answerObject) {
      const isAnswerValue =
        answerObject[`${selectedCell.sheet}!${selectedCell.cell}`];
      if (isAnswerValue) {
        setCurrentSelectedCellAnswer(isAnswerValue);
      } else {
        setCurrentSelectedCellAnswer(null);
      }
    } else {
      setCurrentSelectedCellAnswer(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCell]);

  const saveManifestFile = async (type: string) => {
    const docId = currentDoc?._id;
    if (!docId) return;
    try {
      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=${type}`,
        {
          documentId: `${docId}`
        },
        auth
      );
      if (presignedUrl.data.url !== 'locked') {
        const jsonPayload = JSON.stringify(manifestValue);
        const blob = new Blob([jsonPayload], { type: 'application/json' });
        const putResult = await axios.put(presignedUrl.data.url, blob, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        logger.info('put result', putResult);
      } else {
        throw Error('File Locked by another user.');
      }
    } catch (e) {
      const error = e as Error;
      logger.error('error from save manifest', error.message);
    }
  };

  const handleFinalize = async () => {
    if (!answerObject || !manifestValue) return;
    setFinalizeLoading(true);
    try {
      logger.info('Finalizing');
      await handleSaveDocument();

      // handle creating new qanda sentences in the database.
      const sentences = manifestValue.questions.map((ele) => {
        const answerCitations = createCitationInfo(
          answerObject[`${ele.answerSheet}!${ele.answerCell}`]!.answer[0]
            .textInternal!,
          ele.context
        );
        logger.info('Answer Citations', { answerCitations });
        return {
          text: ele.questionText,
          answer:
            answerObject[`${ele.answerSheet}!${ele.answerCell}`]!.answer.at(-1)!
              .text,

          originalInternalAnswer: answerCitations.originalInternalAnswer,
          originalCitations: answerCitations.allCitationIds,
          originalHardCitations: answerCitations.citationArray,
          embedding: ele.embedding
        };
      });
      logger.info('sentences', sentences);
      //! ADD BACK
      //TODO add back
      await axios.post(
        `${process.env.REACT_APP_KORU_API}/sentence?type=addQA`,
        {
          documentId: currentDoc?._id,
          sentences: sentences
        },
        auth
      );
      const result = await updateDocument(
        { finalized: true, status: 'finalized' },
        currentDoc?._id!
      );
      setCurrentDoc(result);
      logger.info('Result from doc update', result);
      setFinalizeLoading(false);
    } catch (e) {
      setFinalizeLoading(false);
      setMainAlert({
        showAlert: true,
        message: 'Document Finalize Error',
        alertType: 'error'
      });
    }
  };

  const handleUpdateFromEdit = (text: string, cell: ISelectedCell) => {
    if (!manifestValue) return;
    const temp = manifestValue;
    const idx = temp.questions.findIndex(
      (ele: { answerSheet: string; answerCell: string }) =>
        ele.answerSheet === cell.sheet && ele.answerCell === cell.cell
    );
    logger.info('index of answer', idx);
    if (idx !== -1) {
      const currentLastAnswer = temp.questions[idx].answer.at(-1);
      if (currentLastAnswer?.text !== text) {
        temp.questions[idx].answer.push({
          text: text,
          user: user.name
        });
        logger.info('temp at index', temp.questions[idx]);
        setManifestValue({ ...temp });
        validateAnswer();
      }
    }
  };

  const OutputButtonDisabled = () => {
    if (
      (selectedCellsForAnswers?.sheet === selectInputRows[0]?.sheet &&
        selectedCellsForAnswers?.cell === selectInputRows[0]?.cells) ||
      !selectInputRows[0]?.cells ||
      !selectInputRows[0].cells
    ) {
      return true;
    }
    return false;
  };

  function highLightNonValidatedCells(): void {
    if (!manifestValue || !showSheet) return;
    const spreadsheet = spreadsheetRef.current;
    if (!spreadsheet) return;
    for (const question of manifestValue.questions) {
      const cellAddress = `${question.questionSheet}!${question.answerCell}`;
      if (!cellAddress) continue;
      if (!question.userValidated && highLight) {
        spreadsheet?.cellFormat({ backgroundColor: 'yellow' }, cellAddress);
      } else {
        spreadsheet?.cellFormat({ backgroundColor: 'white' }, cellAddress);
      }
    }
  }

  const columnsValid = useMemo(() => {
    if (InputRowsSet.size === 0 || !answerColumn) return false;
    for (const row of InputRowsSet) {
      if (getColumnValue(row) === answerColumn) return false;
    }
    return true;
  }, [InputRowsSet, answerColumn]);

  //* Theses are sub components, in the same file so they can call spreadsheet and save functionality

  const ButtonBar = () => {
    return (
      <>
        {currentDoc?.processed ? (
          <div className="flex flex-row justify-end space-x-2">
            <div className="p-4">
              <Button
                variant={highLight ? 'primary' : 'normal'}
                onClick={() => setHighLight(!highLight)}
              >
                {!highLight ? 'Highlight Non Validated' : 'Remove Highlights'}
              </Button>
            </div>
            <div className="p-4">
              <Button
                onClick={() => handleSaveDocument()}
                loading={draftSaveLoading}
              >
                Save Draft
              </Button>
            </div>

            <div className="p-4">
              <Button
                disabled={!allAnswersValidated}
                onClick={() => handleFinalize()}
                loading={finalizeLoading}
              >
                Finalize
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-4 flex justify-end space-x-2">
            {/* <Button
              disabled={!(InputRowsSet.size > 0)}
              onClick={() => testAnswerValidation()}
            >
              Get Answer values
            </Button> */}

            {currentDoc?.documentType === 'rfp' && (
              <Button
                onClick={() => createDataForUpload()}
                disabled={canUpload ? false : true}
                loading={uploadProcessing}
              >
                Upload for Processing
              </Button>
            )}
            {currentDoc?.documentType === 'prev-rfp' && (
              <Button
                // onClick={() => createDataForPrevFileUpload()}
                disabled={canUpload ? false : true}
                loading={uploadProcessing}
              >
                Upload & Finalize
              </Button>
            )}
          </div>
        )}
      </>
    );
  };

  const VerifySideShow = () => {
    return (
      <div className="w-full bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-sm text-sm text-gray-800 flex flex-col space-y-6">
        {!columnsValid && inputSelected && outputSelected && (
          <div className="w-full flex flex-row justify-between bg-red-200 border border-red-800 rounded-lg p-2 text-red-800">
            <p className="p-1">
              The Question and Answer Columns cannot be the same
            </p>
            <SquareX
              size={18}
              onClick={() => {
                setAnswerColumn('');
                setOutputSelected(false);
              }}
            />
          </div>
        )}
        <div className="w-full flex flex-row items-center justify-center relative">
          <div className="flex flex-row justify-center space-x-8">
            <div className="flex flex-col items-center space-y-4">
              <Button
                disabled={InputRowsSet.size === 0 || inputSelected}
                variant={inputSelected ? 'primary' : 'normal'}
                onClick={() => {
                  setInputSelected(true);
                }}
              >
                {!inputSelected ? 'Set Questions Range' : 'Questions Range'}
              </Button>
              {/* {selectInputRows[0]?.cells && (
                <p className="bg-yellow-300 p-2">{selectInputRows[0]?.cells}</p>
              )} */}
              {groupAdjacentCells(InputRowsSet).length > 0 && (
                <>
                  {groupAdjacentCells(InputRowsSet).map((ele) => (
                    <p className="bg-yellow-300 p-2">{ele}</p>
                  ))}
                </>
              )}
            </div>

            <div className="flex flex-col items-center space-y-4">
              <Button
                disabled={!inputSelected || outputSelected}
                variant={outputSelected ? 'primary' : 'normal'}
                onClick={() => setOutputSelected(true)}
              >
                {!outputSelected ? 'Set Answer Range' : 'Answer Range'}
              </Button>

              {answerColumn && (
                <>
                  {replaceColumnInRanges(
                    groupAdjacentCells(InputRowsSet),
                    answerColumn
                  ).map((ele) => (
                    <p className="bg-yellow-300 p-2">{ele}</p>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className="absolute right-0 top-0">
            <SquareX
              size={18}
              onClick={() => {
                setInputRowsSet(new Set<string>());
                setAnswerColumn('');
                setInputSelected(false);
                setOutputSelected(false);
              }}
            />
          </div>
        </div>

        <div className="flex flex-col space-y-2">
          <h3 className="text-md font-semibold text-blue-700">
            Select Question and Answer Ranges
          </h3>
          <p>
            Please select the range/s for the <strong>Questions</strong>. If the
            question span different sections, hold the Alt / Opt button down
            while selecting multiple sections.
            <strong> Answers</strong>, select any range of cells in the answer
            column. The answers will be placed in the corresponding cells.
          </p>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!currentLock?.expireAt) return;

    hasSavedAt25.current = false; // Reset when lock changes
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const expires = new Date(currentLock.expireAt).getTime();
      const diff = Math.max(0, expires - now);
      const minutes = Math.floor(diff / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setRemainingTime({ minutes, seconds });
      if (minutes < 5) {
        setWarningColor('red');
        setTimeWarning(true);
      }
      if (minutes === 5 && !hasSavedAt25.current) {
        handleSaveDocument();
        hasSavedAt25.current = true;
      }

      if (diff <= 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [currentLock]);

  const LockCountdownTimer = () => {
    return (
      <>
        {showTimeWarning && (
          <div
            className={`bg-${warningColor}-100 border border-${warningColor}-300 text-${warningColor}-800 px-4 py-2 rounded shadow text-sm z-50 flex flex-row justify-between items-center`}
          >
            <span>
              <strong>Lock Expires In:</strong> {remainingTime?.minutes}m
              {remainingTime?.seconds}s
              {hasSavedAt25.current && (
                <span className="ml-2 text-xs italic text-gray-600">
                  (autosaved at 5m)
                </span>
              )}
            </span>
            <span
              className="ml-4 cursor-pointer font-bold"
              onClick={() => setTimeWarning(false)}
            >
              ×
            </span>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="h-[100%] w-[100%] resize overflow-auto">
      {currentLock && remainingTime?.minutes && remainingTime?.minutes < 30 && (
        <LockCountdownTimer />
      )}
      <div
        style={{
          visibility: showSheet ? 'visible' : 'hidden',
          // border: '1px solid red',
          height: '85vh',
          // width: '100%',
          display: 'flex',
          flexDirection: 'row'
        }}
        // className="gap-4 p-4"
      >
        <div className="flex w-[65%] h-[100%]">
          <SpreadsheetComponent
            width={'100%'}
            height={'100%'}
            allowOpen={true}
            ref={spreadsheetRef}
            cellSave={cellSave}
            allowResizing={true}
            onChange={onActionComplete}
            // openSettings={{}}
            readOnly={!currentDoc?.processed ? true : false}
            openUrl="https://spreadsheet.korucompliance.com/api/spreadsheet/open"
            saveUrl="https://spreadsheet.korucompliance.com/api/spreadsheet/save"
            openComplete={handleOpenComplete}
            showFormulaBar={false}
            showRibbon={false}
            allowFiltering={false}
            select={handleCellSelect}
            allowSave={!currentDoc?.finalized}
            beforeSave={handleBeforeSave}
            saveComplete={saveComplete}
            allowEditing={!currentDoc?.finalized && !docLockedByOther}
            allowWrap={true}
          />
        </div>
        {/* side panel */}
        <div className="w-[35%] flex flex-col border border-black-100 p-2 items-center overflow-hidden h-full">
          <div className="flex flex-col justify-between h-full w-full">
            <div>
              <div className="w-full bg-gray-100 border border-gray-300 rounded px-4 py-2 mb-4 shadow-sm text-center">
                <h1 className="text-xl font-semibold text-gray-800">
                  Document Controls
                </h1>
              </div>
              {alertA.showAlert ? (
                <div className="p-4">
                  <Alert
                    type="warning"
                    dismissible={true}
                    onDismiss={() =>
                      setAlert({
                        showAlert: false,
                        message: '',
                        type: 'success'
                      })
                    }
                  >
                    {alertA.message}
                  </Alert>
                </div>
              ) : null}

              <div className="w-full bg-white border border-gray-200 rounded-md p-3 shadow text-sm text-gray-700 mb-2">
                <p>
                  <span className="font-semibold text-gray-600">File:</span>{' '}
                  <span className="text-blue-600 font-medium">
                    {currentDoc?.documentFileName}
                  </span>
                </p>
              </div>
              <div className="h-[60vh] overflow-y-scroll">
                {!currentDoc?.processed &&
                  !docLockedByOther &&
                  spreadsheetReady &&
                  currentDoc &&
                  currentDoc.status === 'needs confirmation' && (
                    <div className="space-y-2">
                      <VerifySideShow />
                      {currentDoc.documentType === 'rfp' && (
                        <DocumentFilter
                          KBChecked={KBChecked}
                          setKBChecked={setKBChecked}
                          kbFilters={kbFilters}
                          setKBFilters={setKBFilters}
                          prevChecked={prevChecked}
                          setPrevChecked={setPrevChecked}
                          selectedDocuments={selectedDocuments}
                          setSelectedDocuments={setSelectedDocuments}
                          similarityThreshold={similarityThreshold}
                          setSimilarThreshold={setSimilarThreshold}
                          type={'rfp'}
                        />
                      )}
                      {currentDoc?.documentType === 'prev-rfp' && (
                        <div
                          className="w-full h-[35vh] bg-blue-50 overflow-y-scroll border border-blue-200 rounded-lg p-4 shadow-sm text-sm text-gray-800 flex flex-col space-y-6 relative"
                          style={{ scrollbarWidth: 'none' }}
                        >
                          <p>
                            This file will be uploaded and the Questions and
                            Answers will be included in Previous Questionnaires.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                {currentDoc?.processed && (
                  <div className="flex-grow h-[50vh]">
                    {!currentSelectedCellAnswer ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-sm text-sm text-gray-800 space-y-2">
                        <h3 className="text-lg font-semibold text-blue-700">
                          Review Mode Instructions
                        </h3>
                        <p>
                          Select a cell from the answer range to view its
                          suggested response. If our system found potential
                          issues or lacked confidence, you'll see specific
                          reasons explained.
                        </p>
                        <p>
                          Once you're satisfied, click <strong>"Accept"</strong>{' '}
                          to populate the cell with the selected answer.
                        </p>
                      </div>
                    ) : (
                      // <div className="w-full bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-sm text-sm text-gray-800 break-words whitespace-normal">
                      <RFPAnswerCard
                        data={currentSelectedCellAnswer}
                        acceptAnswer={acceptAnswer}
                        validateAnswer={validateAnswer}
                        currentCell={selectedCell}
                        handleUpdateFromEdit={handleUpdateFromEdit}
                        highLightNonValidatedCells={highLightNonValidatedCells}
                      />
                      // </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="pt-4">
                {!docLockedByOther &&
                  spreadsheetReady &&
                  currentDoc &&
                  !currentDoc.finalized && <ButtonBar />}
              </div>
              <div className="w-full">
                {docLockedByOther || currentDoc?.finalized ? (
                  <div className="mt-4 w-full text-center bg-yellow-100 border border-yellow-300 text-yellow-800 px-4 py-2 rounded shadow">
                    <strong>View Only Mode:</strong> This document is locked or
                    finalized. You can view the file, but editing is disabled.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      {loadingDocs && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-800 bg-opacity-50">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4">Processing File</p>
        </div>
      )}
      <Modal
        visible={showDocLockModal}
        onDismiss={() => setShowDocLockModal(false)}
      >
        <p>This document is currently in use by someone else.</p>
        <p>You can view the file but not edit it.</p>
        <p>For more info contact the current user {currentLock?.email}</p>
      </Modal>
      <Modal
        visible={coinsModal}
        onDismiss={() => {
          setCoinsModal(false);
          setAvailableCoins(0);
          setCoinsRequired(0);
        }}
      >
        <p>This RPF will need {coinsRequired} Coins.</p>
        <p>You currently have {availableCoins} Coins Available</p>
      </Modal>
      <Modal
        visible={showCoinsModal.visible}
        onDismiss={() => setShowCoinsModal({ visible: false, type: '' })}
      >
        <>
          {showCoinsModal.type === 'BuyMore' ? (
            <div>
              <p className="text-sm text-gray-700 mb-4">
                You do not have enough Compliance Credits to perform this
                document analysis. To continue using advanced features like
                document analysis and AI answers, additional credits are
                required.
              </p>
              {user?.isAdmin ? (
                <Button
                  onClick={() => {
                    setShowCoinsModal({ visible: false, type: '' });
                    setSubsAlert('', 'coins');
                  }}
                  // className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
                >
                  Buy More Credits
                </Button>
              ) : (
                <button
                  className="bg-gray-400 text-white font-medium py-2 px-4 rounded cursor-not-allowed"
                  disabled
                >
                  Contact your administrator to purchase more credits
                </button>
              )}
            </div>
          ) : null}
        </>
      </Modal>
    </div>
  );
};

export default SpreadsheetUploader;
